import { Link } from "react-router-dom";
import { CANON_COLORS, CAT_COLORS, imgSrc } from "../lib/api";
import { User, HelpCircle } from "lucide-react";

const CanonBadge = ({ canon }) => {
  const c = CANON_COLORS[canon] || CANON_COLORS["Incerto"];
  return (
    <span className="chip" style={{ background: c.bg, color: c.color }} data-testid="canon-badge">
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: c.dot }} />
      {canon}
    </span>
  );
};

const FruitGlyph = ({ categoria, size = 56 }) => {
  const color = CAT_COLORS[categoria] || CAT_COLORS["Sconosciuta"];
  const letter = (categoria || "?")[0];
  return (
    <span
      className="font-display font-bold flex items-center justify-center rounded-2xl shrink-0"
      style={{
        width: size, height: size, color: "#fff", fontSize: size * 0.42,
        backgroundImage: `linear-gradient(140deg, ${color}, ${color}cc)`,
        boxShadow: `0 10px 22px -10px ${color}aa`,
      }}
      aria-hidden="true"
    >
      {letter}
    </span>
  );
};

const ownerText = (f) => {
  const po = (f.possessore_attuale || "").trim();
  if (!po || po.toLowerCase().includes("sconosciut")) return null;
  return po;
};

export const FruitCard = ({ fruit }) => {
  const owner = ownerText(fruit);
  const cat = CAT_COLORS[fruit.categoria] || CAT_COLORS["Sconosciuta"];
  return (
    <Link to={`/frutto/${fruit.slug}`} data-testid={`fruit-card-${fruit.slug}`}
      className="luma-card luma-card-hover p-4 sm:p-5 flex flex-col gap-3 rise">
      {fruit.immagine && (
        <div className="rounded-xl overflow-hidden flex items-center justify-center"
          style={{ background: "#F7FBFF", border: "1px solid var(--luma-line)", aspectRatio: "16 / 10" }}
          data-testid={`fruit-card-image-${fruit.slug}`}>
          <img src={imgSrc(fruit.immagine)} alt={`Frutto ${fruit.nome_romaji}`} loading="lazy"
            className="w-full h-full object-contain" />
        </div>
      )}
      <div className="flex items-start gap-3">
        {!fruit.immagine && <FruitGlyph categoria={fruit.categoria} />}
        <div className="min-w-0 flex-1">
          <h3 className="font-display font-bold text-base sm:text-lg luma-ink leading-tight truncate">
            {fruit.nome_romaji}
          </h3>
          {fruit.nome_it && <p className="text-xs luma-muted truncate">{fruit.nome_it}</p>}
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5">
        <span className="chip" style={{ background: `${cat}1a`, color: cat }}>{fruit.categoria}</span>
        {fruit.sottocategoria && fruit.sottocategoria !== "—" && (
          <span className="chip chip-outline">{fruit.sottocategoria}</span>
        )}
      </div>

      <div className="flex items-center gap-1.5 text-sm mt-auto pt-1">
        {owner ? (
          <>
            <User size={14} className="luma-muted shrink-0" />
            <span className="luma-ink font-semibold truncate">{owner}</span>
          </>
        ) : (
          <>
            <HelpCircle size={14} className="luma-muted shrink-0" />
            <span className="luma-muted italic">Possessore sconosciuto</span>
          </>
        )}
      </div>

      <div className="flex items-center justify-between">
        <CanonBadge canon={fruit.canonicita} />
        {!fruit.verificato && (
          <span className="label-mono luma-muted" style={{ fontSize: ".58rem" }}>Da verificare</span>
        )}
      </div>
    </Link>
  );
};

export const FruitRow = ({ fruit }) => {
  const owner = ownerText(fruit);
  const cat = CAT_COLORS[fruit.categoria] || CAT_COLORS["Sconosciuta"];
  return (
    <Link to={`/frutto/${fruit.slug}`} data-testid={`fruit-row-${fruit.slug}`}
      className="luma-card luma-card-hover px-4 py-3 flex items-center gap-3 rise">
      <FruitGlyph categoria={fruit.categoria} size={40} />
      <div className="min-w-0 flex-1">
        <h3 className="font-display font-bold text-sm sm:text-base luma-ink leading-tight truncate">
          {fruit.nome_romaji}
        </h3>
        <p className="text-xs luma-muted truncate">
          {fruit.categoria}{fruit.sottocategoria !== "—" ? ` · ${fruit.sottocategoria}` : ""}
          {owner ? ` · ${owner}` : " · Possessore sconosciuto"}
        </p>
      </div>
      <div className="shrink-0 hidden sm:block">
        <CanonBadge canon={fruit.canonicita} />
      </div>
    </Link>
  );
};
